"use client";

import { ReactNode } from "react";
import {
  SelectableCard,
  SelectableCheckbox,
  Typography,
} from "@drpcrd/shared/ui";

type Item = {
  id: string;
  title: string;
  description?: string;
  leftElement?: ReactNode;
  statusBadge?: ReactNode;
};

type Props = {
  items: Item[];
  onItemSelectAction: (id: string) => void;
  selectionMode?: "single" | "multiple";
  selectedItemId?: string;
  selectedItemIds?: string[];
  emptyMessage?: string;
  disabled?: boolean;
};

const getLeftElement = (
  item: Item,
  isSelected: boolean,
  isSingleSelection: boolean,
): ReactNode => {
  if (item.leftElement) {
    return item.leftElement;
  }
  // Only show checkbox for multiple selection mode, as single selection is indicated by the card border
  if (!isSingleSelection) {
    return <SelectableCheckbox isSelected={isSelected} />;
  }
  return undefined;
};

/**
 * Renders a selectable list of items for the creator page.
 */
export const CreatorPageSelectableListRenderer = ({
  items,
  onItemSelectAction,
  selectionMode = "multiple",
  selectedItemId,
  selectedItemIds,
  emptyMessage = "No items available",
  disabled = false,
}: Props) => {
  const isSingleSelection = selectionMode === "single";

  // Fallback for empty items
  if (items.length === 0) {
    return (
      <Typography size="lg" color="neutral">
        {emptyMessage}
      </Typography>
    );
  }

  return (
    <div className="flex h-fit w-full flex-col gap-1">
      {items.map((item) => {
        const isSelected = isSingleSelection
          ? selectedItemId === item.id
          : (selectedItemIds?.includes(item.id) ?? false);

        return (
          <SelectableCard
            key={item.id}
            isSelected={isSelected}
            title={item.title}
            description={item.description}
            onSelect={() => onItemSelectAction(item.id)}
            disabled={disabled}
            leftElement={getLeftElement(item, isSelected, isSingleSelection)}
            statusBadge={item.statusBadge}
          />
        );
      })}
    </div>
  );
};
